/**
 * MeshDetailsModal — self-contained portal modal (backdrop + frame in this
 * module's own SCSS). Does NOT use the shared <Modal> component because that
 * relies on global `.modal-*` rules from editor/style/textureModal.scss,
 * which aren't loaded in the generator or bollardbuddy bundles.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import * as Tooltip from '@radix-ui/react-tooltip';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@shared/services/firebase.js';
import { DownloadIcon, TrashIcon, Cross24Icon } from '@shared/icons';
import { composeAttributionString } from '@shared/asset-upload/extractGlbAttribution.js';
import assetsService from '../services/assetsService.js';
import {
  formatBytes,
  formatDate,
  getOptimizationDisplay,
  getServedUrl
} from '../utils.js';
import { isEditableTarget } from '@shared/utils/dom.js';
import { useSharedMessages } from '@shared/i18n/sharedMessages.js';
import {
  clearReoptimizeRun,
  getReoptimizeRun,
  startReoptimizeRun,
  subscribeReoptimizeRuns
} from '../reoptimizeRuns.js';
import { getCopyRun, startCopyRun, subscribeCopyRuns } from '../copyRuns.js';
import styles from './MeshDetailsModal.module.scss';

// RAD transcode job statuses, mapped to their shared-message ids.
const JOB_STATUS_MESSAGE = {
  queued: 'meshJobQueued',
  running: 'meshJobRunning',
  saving: 'meshJobSaving'
};

// Stages reported by reoptimizeAsset(), mapped to their shared-message ids
// so the button can say what it is doing in the user's language.
const REOPTIMIZE_STAGE_MESSAGE = {
  downloading: 'reoptimizePreparing',
  optimizing: 'reoptimizeOptimizing',
  uploading: 'reoptimizeUploading'
};

// `ok: false` reasons from reoptimizeAsset, mapped to copy. The pipeline
// reports them as snake_case codes; they never reach the user verbatim.
// Anything unmapped falls back to the generic reoptimizeNoChange line.
const REOPTIMIZE_NO_WIN_MESSAGE = {
  not_smaller_than_current: 'reoptimizeAlreadyOptimal',
  not_smaller: 'reoptimizeAlreadyOptimal',
  already_optimized: 'reoptimizeAlreadyOptimized',
  timeout: 'reoptimizeTimedOut',
  worker_error: 'reoptimizeWorkerError'
};
// These are the pipeline giving up, not a verdict on the model, so they
// render in the error style like a thrown failure would.
const REOPTIMIZE_PIPELINE_FAILURES = new Set(['timeout', 'worker_error']);

// Stages reported by copyAssetToLibrary (its own 'downloading', then
// uploadAsset's), mapped to shared-message ids for the operation indicator.
const COPY_STAGE_MESSAGE = {
  downloading: 'copyStagePreparing',
  validating: 'copyStagePreparing',
  optimizing: 'copyStageOptimizing',
  uploading: 'copyStageUploading',
  thumbnailing: 'copyStageFinishing'
};

// User-editable attribution fields. `title` deliberately is NOT here — the
// asset doc's `name` (Display name) is the single source of truth for the
// model title. sourceName / generator are diagnostic, surfaced read-only.
const ATTRIBUTION_FIELDS = ['author', 'license', 'source'];

const EMPTY_ATTRIBUTION = {
  author: '',
  license: '',
  source: '',
  sourceName: '',
  generator: ''
};

function pickAttribution(doc) {
  const a = doc?.attribution || {};
  return {
    author: a.author || '',
    license: a.license || '',
    source: a.source || '',
    sourceName: a.sourceName || '',
    generator: a.generator || ''
  };
}

function attributionEquals(a, b) {
  for (const key of ATTRIBUTION_FIELDS) {
    if ((a[key] || '') !== (b[key] || '')) return false;
  }
  return true;
}

// True if the drawn frame is essentially uniform — i.e. nothing rendered: the
// WebGL canvas was captured before the splat drew (pure black), or it shows only
// the flat #393939 background. A real splat spans a wide luminance range, so we
// reject a near-zero min→max spread. Samples a sparse stride for speed.
// Best-effort: if the pixels can't be read, don't block the capture.
function isFrameBlank(ctx, w, h) {
  try {
    const { data: px } = ctx.getImageData(0, 0, w, h);
    let min = 255;
    let max = 0;
    const stride = 4 * 101; // sparse, prime-ish sample for speed
    for (let i = 0; i + 2 < px.length; i += stride) {
      const lum = px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114;
      if (lum < min) min = lum;
      if (lum > max) max = lum;
    }
    return max - min < 6;
  } catch {
    return false;
  }
}

const IconTooltip = ({ children, label }) => (
  <Tooltip.Root delayDuration={150}>
    <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        side="top"
        sideOffset={6}
        className={styles.tooltipContent}
      >
        {label}
        <Tooltip.Arrow className={styles.tooltipArrow} />
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);
IconTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired
};

const MeshDetailsModal = ({
  assetId,
  ownerUid,
  onClose,
  onPlace,
  onCopied,
  currentIndex,
  totalItems,
  onNavigate
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // The asset's processing/transcode jobs (today: the RAD/LOD optimization).
  // One source asset, N jobs — owner-only by rules.
  const [assetJobs, setAssetJobs] = useState([]);
  // Briefly true after a successful "Recapture thumbnail" click, to flash a
  // checkmark on the button as feedback (the live capture is otherwise silent).
  const [thumbCaptured, setThumbCaptured] = useState(false);
  // Reoptimize status lives in a module-level registry (reoptimizeRuns.js),
  // keyed by asset, rather than in this component: a run keeps going after
  // the modal closes or navigates to another asset, and work that keeps
  // going must stay visible. Any instance showing that asset — including
  // one opened later — reads the same progress and outcome.
  const reoptimizeRun = useSyncExternalStore(subscribeReoptimizeRuns, () =>
    getReoptimizeRun(assetId)
  );
  // Outcome of the "Remove optimized" action; local because it is instant.
  const [removeStatus, setRemoveStatus] = useState(null);
  // "Copy to my library" (non-owners). Registry-backed like reoptimize: the
  // copy outlives the modal, and a reopened modal must show where it got to.
  const copyRun = useSyncExternalStore(subscribeCopyRuns, () =>
    getCopyRun(assetId)
  );
  const t = useSharedMessages();

  const [name, setName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [attribution, setAttribution] = useState(EMPTY_ATTRIBUTION);
  const [savedAttribution, setSavedAttribution] = useState(EMPTY_ATTRIBUTION);
  const [editingAttribution, setEditingAttribution] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Latest close handler, read by the keydown effect (which is set up once).
  const onCloseRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEditingAttribution(false);
    assetsService
      .getAsset(assetId, ownerUid)
      .then((doc) => {
        if (cancelled) return;
        setData(doc || null);
        const initial = doc?.name || doc?.originalFilename || '';
        setName(initial);
        setSavedName(initial);
        const attr = pickAttribution(doc);
        setAttribution(attr);
        setSavedAttribution(attr);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId, ownerUid]);

  // Load the asset's transcode/optimization jobs (owner-only by rules) so the
  // modal can show optimization status. Best-effort: failure just hides the row.
  useEffect(() => {
    const owner = !!auth.currentUser && auth.currentUser.uid === ownerUid;
    if (!owner) {
      setAssetJobs([]);
      return;
    }
    let cancelled = false;
    assetsService
      .getAssetJobs(assetId, ownerUid)
      .then((js) => {
        if (!cancelled) setAssetJobs(js || []);
      })
      .catch(() => {
        if (!cancelled) setAssetJobs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId, ownerUid]);

  // Keyboard nav — mirrors AssetsModal.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't hijack arrow keys while the user is typing in a field (e.g.
      // editing the title / attribution) — let the caret move instead.
      if (e.key !== 'Escape' && isEditableTarget(e.target)) return;
      if (e.key === 'ArrowLeft' && onNavigate) onNavigate('prev');
      else if (e.key === 'ArrowRight' && onNavigate) onNavigate('next');
      else if (e.key === 'Escape') onCloseRef.current?.();
    };
    // Capture phase — see AssetsModal for why (SceneGraph's onKeyDown
    // stopPropagation()s arrow keys in bubble phase before they reach window).
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onNavigate]);

  // Lazy thumbnail backfill: the viewer iframe (splat-viewer or model-viewer)
  // captures a frame of the loaded asset and postMessages it up. If it has no
  // thumbnail yet and we're the owner (only the owner may write to their asset
  // path), upload it so the gallery card stops showing a placeholder. Covers
  // splats and server-generated meshes (which skip the upload-time capture).
  // Best-effort, at most once per asset (assetUpdated then refreshes the card).
  const thumbUploadedRef = useRef(null);
  useEffect(() => {
    const isOwnerNow = !!auth.currentUser && auth.currentUser.uid === ownerUid;
    if (!data || !isOwnerNow) return;
    if (data.type !== 'splat' && data.type !== 'mesh') return;
    if (data.thumbnailUrl || thumbUploadedRef.current === assetId) return;
    const expectedSrc = getServedUrl(data);
    const isSplatAsset = data.type === 'splat';
    const onMessage = (e) => {
      const msg = e.data;
      if (
        !msg ||
        msg.type !==
          (isSplatAsset
            ? '3dstreet:splat-thumbnail'
            : '3dstreet:mesh-thumbnail') ||
        !(msg.blob instanceof Blob)
      ) {
        return;
      }
      // The viewer echoes the src it captured; ignore a stale capture that
      // arrived after the user navigated to a different asset.
      if (msg.src && msg.src !== expectedSrc) return;
      if (thumbUploadedRef.current === assetId) return;
      thumbUploadedRef.current = assetId;
      import('@shared/asset-upload')
        .then(({ uploadCapturedThumbnail }) =>
          uploadCapturedThumbnail(
            assetId,
            ownerUid,
            msg.blob,
            isSplatAsset ? 'splats' : 'meshes'
          )
        )
        .catch((err) =>
          console.warn('[MeshDetailsModal] thumbnail upload failed', err)
        );
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [data, assetId, ownerUid]);

  const iframeRef = useRef(null);
  const isOwner = !!auth.currentUser && auth.currentUser.uid === ownerUid;

  // Synchronous fallback for when the user closes before the viewer's better
  // auto-framed capture fires: read whatever the iframe canvas currently shows
  // (same-origin) and upload it. Lower quality (live view, maybe mid-LOD) but
  // guarantees a thumbnail. Must run while the modal is still open — a ref read
  // in an unmount effect would already be nulled — so it's wired through the
  // explicit close affordances via handleClose, not a cleanup.
  // Read the live preview iframe's canvas (same-origin), downscale to <=512px,
  // and upload it as the asset thumbnail. Captures exactly what's on screen —
  // no iframe reload / file re-download. Shared by the on-close fallback and
  // the explicit "Recapture thumbnail" button. uploadCapturedThumbnail writes a
  // fresh thumbnailUrl + emits assetUpdated, so the gallery card refreshes.
  // Returns: 'ok' (upload kicked off), 'blank' (nothing rendered yet), or
  // 'unavailable' (canvas not readable).
  const uploadLiveCanvasThumbnail = () => {
    try {
      const glCanvas =
        iframeRef.current?.contentDocument?.querySelector('canvas');
      if (!glCanvas || !glCanvas.width || !glCanvas.height) {
        return 'unavailable';
      }
      const maxDim = 512;
      const scale = Math.min(
        1,
        maxDim / Math.max(glCanvas.width, glCanvas.height)
      );
      const tw = Math.max(1, Math.round(glCanvas.width * scale));
      const th = Math.max(1, Math.round(glCanvas.height * scale));
      const tmp = document.createElement('canvas');
      tmp.width = tw;
      tmp.height = th;
      const tctx = tmp.getContext('2d');
      tctx.drawImage(glCanvas, 0, 0, tw, th);
      // The viewer only begins rendering on its first animation frame; before
      // that the GL canvas is an uncleared (black) buffer. Skip a blank/uniform
      // frame rather than persist a black thumbnail that then sticks.
      if (isFrameBlank(tctx, tw, th)) return 'blank';
      thumbUploadedRef.current = assetId;
      tmp.toBlob(
        (blob) => {
          if (!blob) return;
          import('@shared/asset-upload')
            .then(({ uploadCapturedThumbnail }) =>
              uploadCapturedThumbnail(assetId, ownerUid, blob, 'splats')
            )
            .catch(() => {});
        },
        'image/jpeg',
        0.82
      );
      return 'ok';
    } catch {
      return 'unavailable';
    }
  };

  // On-close fallback: if the viewer's better auto-framed capture never fired
  // (user closed before it ran) and the asset still has no thumbnail, persist
  // whatever the live canvas currently shows. Must run while the modal is still
  // open — a ref read in an unmount effect would already be nulled.
  const captureCurrentFrame = () => {
    if (!data || data.type !== 'splat' || !isOwner) return;
    if (data.thumbnailUrl || thumbUploadedRef.current === assetId) return;
    uploadLiveCanvasThumbnail();
  };

  const handleClose = () => {
    captureCurrentFrame();
    onClose();
  };
  // Keep the ref pointing at the latest handleClose. Writing it during render
  // trips react-hooks/refs; an effect runs after commit, which is the supported
  // pattern for a "latest value" ref read by the set-up-once keydown effect.
  useEffect(() => {
    onCloseRef.current = handleClose;
  });
  const nameDirty = isOwner && name.trim() !== savedName && name.trim() !== '';
  const attributionDirty =
    isOwner && !attributionEquals(attribution, savedAttribution);
  const dirty = nameDirty || attributionDirty;

  const onSave = async () => {
    if (!dirty || !data) return;
    setSaving(true);
    setError(null);
    try {
      const updates = {};
      let nextName = savedName;
      if (nameDirty) {
        nextName = name.trim();
        updates.name = nextName;
      }
      let nextAttribution = savedAttribution;
      if (attributionDirty) {
        const trimmed = ATTRIBUTION_FIELDS.reduce(
          (acc, key) => {
            acc[key] = (attribution[key] || '').trim();
            return acc;
          },
          {
            sourceName: savedAttribution.sourceName || '',
            generator: savedAttribution.generator || ''
          }
        );
        const attributionStr = composeAttributionString(trimmed);
        updates.attribution = {
          ...trimmed,
          attribution: attributionStr,
          attributionUrl: trimmed.source
        };
        nextAttribution = { ...trimmed };
      }
      await assetsService.updateAsset(assetId, ownerUid, updates);
      setData((prev) => (prev ? { ...prev, ...updates } : prev));
      if (nameDirty) setSavedName(nextName);
      if (attributionDirty) {
        setSavedAttribution(nextAttribution);
        setAttribution(nextAttribution);
      }
      setEditingAttribution(false);
    } catch (err) {
      console.error('[MeshDetailsModal] save failed', err);
      setError(err.message || t('meshSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Enter commits pending edits — same as clicking "Save changes" (onSave
  // no-ops when nothing is dirty).
  const onFieldKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    onSave();
  };

  const cancelAttributionEdit = () => {
    setAttribution(savedAttribution);
    setEditingAttribution(false);
  };

  const onDownloadOriginal = () => {
    if (!data?.storageUrl) return;
    window.open(data.storageUrl);
  };

  const onDownloadOptimized = () => {
    if (!data?.optimizedSourceUrl) return;
    window.open(data.optimizedSourceUrl);
  };

  const onDelete = async () => {
    if (!isOwner || !data) return;
    const label = savedName || data.originalFilename;
    if (!window.confirm(t('meshDeleteConfirm', { name: label }))) {
      return;
    }
    try {
      await assetsService.deleteAsset(assetId, ownerUid, false);
      onClose();
    } catch (err) {
      console.error('[MeshDetailsModal] delete failed', err);
      setError(err.message || t('meshDeleteFailed'));
    }
  };

  const onRestore = async () => {
    if (!isOwner || !data) return;
    setError(null);
    try {
      // Soft-delete dropped this asset's bytes from the user's usage tally
      // (asset-quota.js onAssetWritten subtracts deleted docs). Restoring
      // adds them back — so check headroom first.
      const proposedBytes = Number(data.size) || 0;
      try {
        const callable = httpsCallable(functions, 'getUploadQuota');
        const { data: quota } = await callable({ proposedBytes });
        if (quota && quota.allowed === false && !quota.soft) {
          const usedMb = ((quota.bytesUsed || 0) / 1000 / 1000).toFixed(1);
          const limitMb = Math.round((quota.planLimit || 0) / 1000 / 1000);
          const restoreMb = (proposedBytes / 1000 / 1000).toFixed(1);
          setError(
            t('meshRestoreQuota', {
              needed: restoreMb,
              used: usedMb,
              limit: limitMb
            })
          );
          return;
        }
      } catch (quotaErr) {
        // If the callable is unavailable, fall through and let the write
        // attempt — server still tracks usage even if it can't pre-flight.
        console.warn(
          '[MeshDetailsModal] quota check unavailable, proceeding',
          quotaErr
        );
      }

      await assetsService.undeleteAsset(assetId, ownerUid);
      // Refresh local state so the banner and button disappear without
      // closing the modal — the user might want to keep editing.
      setData((prev) => (prev ? { ...prev, deleted: false } : prev));
    } catch (err) {
      console.error('[MeshDetailsModal] restore failed', err);
      setError(err.message || t('meshRestoreFailed'));
    }
  };

  const handlePlace = () => {
    if (!onPlace || !data) return;
    onPlace({
      assetId,
      ownerUid,
      storageUrl: data.storageUrl,
      optimizedSourceUrl: data.optimizedSourceUrl,
      name: savedName || data.name || data.originalFilename || '',
      type: data.type
    });
    handleClose();
  };

  // Recapture the thumbnail from the CURRENT live view — no iframe reload / file
  // re-download. The splat is already rendered in the preview, so we grab that
  // exact frame (orbit to frame the shot first, then click). uploadCapturedThumbnail
  // overwrites the thumbnail with a fresh URL + emits assetUpdated, refreshing
  // the gallery card. thumbUploadedRef is reset first so the forced recapture
  // isn't skipped as "already done".
  const onRegenerateThumbnail = () => {
    if (!isOwner || !data) return;
    setError(null);
    thumbUploadedRef.current = null;
    const result = uploadLiveCanvasThumbnail();
    if (result !== 'ok') {
      setError(t('meshCaptureFailed'));
      return;
    }
    setThumbCaptured(true);
    setTimeout(() => setThumbCaptured(false), 1500);
  };

  // Re-run the current optimization pipeline over the ORIGINAL and repoint the
  // doc at the result. Useful when the asset was uploaded before a pipeline
  // improvement, or when its optimization was skipped (worker timeout) and it
  // has been serving the unoptimized original ever since. A "no win" outcome
  // (pipeline skipped, or nothing smaller than what's already served) is
  // reported as a message, not an error — the asset is left exactly as it was.
  const onReoptimize = () => {
    if (!isOwner || !data || reoptimizeRun?.stage) return;
    setRemoveStatus(null);
    startReoptimizeRun(data, { ownerUid });
  };

  // When a run for the asset on screen finishes, reflect the new variant
  // without closing: the Size row reads optimizedSourceSize, and the
  // preview/download use optimizedSourceUrl. Keyed on the new path so a
  // modal opened after the run already carries the values from its own doc
  // read, and a stale outcome from an earlier run is not re-applied.
  useEffect(() => {
    const result = reoptimizeRun?.result;
    if (!result?.ok || !data || data.optimizedSourcePath === result.newPath) {
      return;
    }
    setData((prev) =>
      prev
        ? {
            ...prev,
            optimizedSourceUrl: result.newUrl,
            optimizedSourcePath: result.newPath,
            optimizedSourceSize: result.bytesAfter,
            optimizationMetadata: result.metadata
          }
        : prev
    );
  }, [reoptimizeRun, data]);

  // The status line under the Size row. A "Remove optimized" outcome is the
  // most recent thing that happened (onReoptimize clears it when a new run
  // starts), so it wins over whatever the run registry still holds; then
  // progress, outcome or failure of the run for this asset.
  const noWinResult =
    reoptimizeRun?.result && !reoptimizeRun.result.ok
      ? reoptimizeRun.result
      : null;
  const noWinIsFailure =
    !!noWinResult && REOPTIMIZE_PIPELINE_FAILURES.has(noWinResult.reason);
  const reoptimizeStatusText = (() => {
    if (removeStatus) return removeStatus.text;
    if (!reoptimizeRun) return null;
    if (reoptimizeRun.stage) {
      return t(REOPTIMIZE_STAGE_MESSAGE[reoptimizeRun.stage]);
    }
    if (reoptimizeRun.error !== undefined) {
      return reoptimizeRun.error || t('reoptimizeFailed');
    }
    const result = reoptimizeRun.result;
    if (!result) return null;
    if (!result.ok) {
      return t(
        REOPTIMIZE_NO_WIN_MESSAGE[result.reason] || 'reoptimizeNoChange'
      );
    }
    return t('reoptimizeDone', {
      size: formatBytes(result.bytesAfter),
      saved: formatBytes(Math.max(0, result.bytesBefore - result.bytesAfter))
    });
  })();
  const reoptimizeStatusIsError = removeStatus
    ? !!removeStatus.error
    : reoptimizeRun?.error !== undefined || noWinIsFailure;

  // Reverse a lossy optimization: drop the optimized fields from the doc so
  // everything serves the untouched original again. The optimized object is
  // orphaned for the storage GC; Optimize brings a variant back.
  const onRemoveOptimized = async () => {
    if (!isOwner || !data?.optimizedSourceUrl || reoptimizeRun?.stage) return;
    try {
      const { removeOptimizedVariant } = await import('@shared/asset-upload');
      await removeOptimizedVariant(data, { ownerUid });
      clearReoptimizeRun(assetId);
      setData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next.optimizedSourceUrl;
        delete next.optimizedSourcePath;
        delete next.optimizedSourceSize;
        delete next.optimizationMetadata;
        return next;
      });
      setRemoveStatus({ text: t('reoptimizeRemoved') });
    } catch (err) {
      console.error('[MeshDetailsModal] remove optimized failed', err);
      setRemoveStatus({
        error: true,
        text: err.message || t('reoptimizeRemoveFailed')
      });
    }
  };

  // Non-owner path to the same outcome as Reoptimize: make an own copy of
  // the original through the normal upload pipeline. GLB only (see
  // copyAsset.js). Signed-out viewers do not get the button.
  const canCopyToLibrary =
    !isOwner &&
    !!auth.currentUser &&
    !loading &&
    !!data &&
    data.type !== 'splat' &&
    !data.deleted;
  const onCopyToLibrary = () => {
    if (!canCopyToLibrary || copyRun?.stage) return;
    startCopyRun(data, { onCopied });
  };
  const copyStatus = (() => {
    if (!copyRun) return null;
    if (copyRun.stage) return { stage: copyRun.stage };
    if (copyRun.error !== undefined) {
      return { error: true, text: copyRun.error || t('copyToLibraryFailed') };
    }
    return {
      done: true,
      text: t(
        copyRun.swapped ? 'copyToLibraryDoneSwapped' : 'copyToLibraryDone'
      )
    };
  })();
  const copyStatusText = copyStatus?.stage
    ? t(COPY_STAGE_MESSAGE[copyStatus.stage] || 'copyToLibraryCopying')
    : (copyStatus?.text ?? null);

  // Inline "re-run the pipeline" control, rendered inside the Size row next
  // to whatever that row says about the current optimization. Owner-only and
  // GLB-only (splats are transcoded server-side by the RAD job instead), and
  // it reports its own progress in place while running.
  const reoptimizeButton = (label) => {
    const isGlb = !!data && data.type !== 'splat';
    if (!isOwner || loading || !isGlb || data?.deleted) return null;
    // Spacing comes from the button's own margin.
    return (
      <button
        type="button"
        onClick={onReoptimize}
        disabled={!!reoptimizeRun?.stage}
        className={styles.retryOptimizeBtn}
      >
        {label}
      </button>
    );
  };

  // Sibling of the button above, only offered while a variant is served.
  const removeOptimizedButton = () => {
    if (!isOwner || loading || !data?.optimizedSourceUrl || data?.deleted) {
      return null;
    }
    return (
      <button
        type="button"
        onClick={onRemoveOptimized}
        disabled={!!reoptimizeRun?.stage}
        className={styles.retryOptimizeBtn}
      >
        {t('reoptimizeRemove')}
      </button>
    );
  };

  // Use mousedown, not click: a `click` fires on the common ancestor of
  // mousedown+mouseup, so dragging from an input inside the modal to a
  // mouseup on the backdrop would land `click` on the backdrop and close.
  // Closing on mousedown only triggers when the press itself starts here.
  const handleBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // This modal serves both meshes (GLB) and splats (.ply/.splat/.spz). The
  // only type-dependent bits are the live viewer page and the type label.
  const isSplat = data?.type === 'splat';
  const viewerPage = isSplat ? '/splat-viewer.html' : '/model-viewer.html';

  // Source format from the original filename / storage path extension. The
  // stored MIME is `application/octet-stream` for every splat (.ply/.splat/.spz/
  // .rad share no registered type), which is meaningless to a user, so show the
  // real format instead, falling back to the raw MIME only if unrecognized.
  // KNOWN_FORMATS.rad doubles as the canonical label for the Optimization row.
  const KNOWN_FORMATS = {
    ply: 'PLY',
    splat: 'SPLAT',
    spz: 'SPZ',
    rad: 'RAD Streaming Level-of-Detail',
    glb: 'GLB',
    gltf: 'glTF'
  };
  const sourceExt = (data?.originalFilename || data?.storagePath || '')
    .split(/[?#]/)[0]
    .split('.')
    .pop()
    ?.toLowerCase();
  const formatLabel =
    (sourceExt && KNOWN_FORMATS[sourceExt]) || data?.mimeType || '—';

  // Optimization (transcode) status. Only shown when there's actually an
  // optimized variant or an in-flight job. An uploaded .rad has neither (it's
  // already the streaming form, which the Format row already says), so no row
  // appears for it.
  const hasOptimizedVariant = !!data?.optimizedSourceUrl;
  const activeOptimizeJob = assetJobs.find((j) =>
    ['queued', 'running', 'saving'].includes(j.status)
  );
  const optimizationLabel = hasOptimizedVariant
    ? isSplat
      ? KNOWN_FORMATS.rad
      : t('meshOptimizedReady')
    : activeOptimizeJob
      ? t('meshOptimizingJob', {
          // Unknown statuses fall through untranslated rather than render a
          // message id — the job's own vocabulary can outgrow this map.
          status: JOB_STATUS_MESSAGE[activeOptimizeJob.status]
            ? t(JOB_STATUS_MESSAGE[activeOptimizeJob.status])
            : activeOptimizeJob.status
        })
      : null;

  // Canonical "{Type} · {Source}" title — matches the gallery card overlay
  // and the image/video modal. The source label is the editable display name,
  // so the live `savedName` takes precedence over `data.name` (which only
  // refreshes after the doc reloads).
  const title = `${isSplat ? t('meshTypeSplat') : t('meshTypeModel')} · ${
    savedName || data?.name || data?.originalFilename || t('meshUntitled')
  }`;
  const showNav = onNavigate && totalItems > 1;
  const hasPrev = showNav && currentIndex > 0;
  const hasNext = showNav && currentIndex < totalItems - 1;

  return createPortal(
    <div className={styles.modal} onMouseDown={handleBackgroundMouseDown}>
      {hasPrev && (
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonPrev}`}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate('prev');
          }}
          title={`${t('meshPreviousItem')} (←)`}
          aria-label={t('meshPreviousItem')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonNext}`}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate('next');
          }}
          title={`${t('meshNextItem')} (→)`}
          aria-label={t('meshNextItem')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      )}

      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.title}>
            <svg
              className={styles.titleIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            {title}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label={t('close')}
          >
            <Cross24Icon />
          </button>
        </div>

        <div className={styles.wrapper}>
          <div className={styles.viewerArea}>
            {!loading && !data && (
              <div className={`${styles.placeholder} ${styles.error}`}>
                {t('meshNotAvailable')}
              </div>
            )}
            {data && (
              <iframe
                ref={iframeRef}
                className={styles.viewerFrame}
                title={
                  savedName || data.originalFilename || t('meshViewerTitle')
                }
                // Don't put the editable name in the iframe URL — the src
                // string drives the iframe's load; baking savedName in
                // would cause the viewer to reload on every Save name
                // click. The iframe title above is enough for a11y.
                src={`${viewerPage}?src=${encodeURIComponent(getServedUrl(data))}`}
              />
            )}
          </div>

          <div className={styles.sidebar}>
            {data?.deleted && (
              <div className={styles.deletedBanner} role="alert">
                <strong>{t('meshDeletedTitle')}</strong>
                <span>{t('meshDeletedBody')}</span>
              </div>
            )}
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="meshAssetName">
                {t('meshDisplayName')}
              </label>
              <input
                id="meshAssetName"
                type="text"
                className={styles.fieldInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={onFieldKeyDown}
                disabled={!isOwner || saving || !data}
              />
            </div>

            <AttributionBlock
              attribution={attribution}
              setAttribution={setAttribution}
              editing={editingAttribution}
              onEnterEdit={() => setEditingAttribution(true)}
              onCancel={cancelAttributionEdit}
              onFieldKeyDown={onFieldKeyDown}
              isOwner={isOwner && !!data}
              disabled={saving || !data}
            />

            {dirty && (
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className={styles.saveNameBtn}
              >
                {saving ? t('meshSaving') : t('meshSaveChanges')}
              </button>
            )}

            <div className={styles.metaList}>
              <div>
                <span className={styles.metaLabel}>{t('meshFieldFile')}</span>
                {data?.originalFilename || '—'}
              </div>
              <div>
                <span className={styles.metaLabel}>{t('meshFieldSize')}</span>
                {(() => {
                  const opt = getOptimizationDisplay(data);
                  // The re-run affordance lives in this row because this row
                  // is the reason to press it: it states what the asset is
                  // serving today ("Optimization skipped", "−42%", or nothing
                  // at all for a pre-pipeline upload).
                  if (opt.skipReason) {
                    return (
                      <>
                        {formatBytes(opt.origSize)}{' '}
                        <span className={styles.optimizationNote}>
                          ({opt.skipReason})
                        </span>
                        {reoptimizeButton(t('reoptimizeRetry'))}
                      </>
                    );
                  }
                  if (opt.optSize) {
                    return (
                      <>
                        {formatBytes(opt.origSize)} → {formatBytes(opt.optSize)}{' '}
                        <span className={styles.optimizationSaved}>
                          (−{opt.savePct}%)
                        </span>
                        {reoptimizeButton(t('reoptimizeAgain'))}
                        {removeOptimizedButton()}
                      </>
                    );
                  }
                  // No optimization metadata at all — a GLB uploaded before
                  // the pipeline existed, so there is no note to follow.
                  return (
                    <>
                      {formatBytes(opt.origSize)}
                      {reoptimizeButton(t('reoptimizeStart'))}
                    </>
                  );
                })()}
              </div>
              {reoptimizeStatusText && (
                <div
                  className={
                    reoptimizeStatusIsError
                      ? styles.reoptimizeStatusError
                      : styles.reoptimizeStatus
                  }
                >
                  {reoptimizeStatusText}
                </div>
              )}
              {optimizationLabel && (
                <div>
                  <span className={styles.metaLabel}>
                    {t('meshFieldOptimization')}
                  </span>
                  {optimizationLabel}
                </div>
              )}
              <div>
                <span className={styles.metaLabel}>{t('meshFieldFormat')}</span>
                {formatLabel}
              </div>
              <div>
                <span className={styles.metaLabel}>
                  {t('meshFieldUploaded')}
                </span>
                {formatDate(data?.uploadedAt || data?.createdAt)}
              </div>
              <div>
                <span className={styles.metaLabel}>
                  {t('meshFieldAssetId')}
                </span>
                {assetId}
              </div>
              <div>
                <span className={styles.metaLabel}>{t('meshFieldOwner')}</span>
                {isOwner ? t('meshOwnerYou') : t('meshOwnerOther')}
              </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {copyStatusText && (
              <div
                className={`${styles.opStatus} ${
                  copyStatus?.error
                    ? styles.opStatusError
                    : copyStatus?.done
                      ? styles.opStatusDone
                      : styles.opStatusRunning
                }`}
                role="status"
                aria-live="polite"
              >
                {copyStatus?.stage ? (
                  <span className={styles.opSpinner} aria-hidden="true" />
                ) : (
                  <span className={styles.opGlyph} aria-hidden="true">
                    {copyStatus?.error ? '!' : '✓'}
                  </span>
                )}
                <span>{copyStatusText}</span>
              </div>
            )}

            <Tooltip.Provider>
              <div className={styles.controlButtons}>
                {canCopyToLibrary && (
                  <IconTooltip label={t('copyToLibraryHint')}>
                    <button
                      type="button"
                      onClick={onCopyToLibrary}
                      disabled={!!copyStatus?.stage}
                      className={styles.secondaryButton}
                    >
                      {copyStatus?.stage
                        ? t('copyToLibraryCopying')
                        : t('copyToLibrary')}
                    </button>
                  </IconTooltip>
                )}
                {isOwner &&
                  !loading &&
                  (data?.deleted ? (
                    <IconTooltip label={t('meshRestore')}>
                      <button
                        type="button"
                        onClick={onRestore}
                        disabled={!data}
                        className={`${styles.iconButton} ${styles.restoreBtn}`}
                        aria-label={t('meshRestore')}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M3 12a9 9 0 1 0 3-6.7" />
                          <polyline points="3 4 3 10 9 10" />
                          <polyline points="12 7 12 12 15 14" />
                        </svg>
                      </button>
                    </IconTooltip>
                  ) : (
                    <IconTooltip label={t('meshDelete')}>
                      <button
                        type="button"
                        onClick={onDelete}
                        disabled={!data}
                        className={`${styles.iconButton} ${styles.deleteBtn}`}
                        aria-label={t('meshDelete')}
                      >
                        <TrashIcon />
                      </button>
                    </IconTooltip>
                  ))}
                {isOwner && !loading && isSplat && !data?.deleted && (
                  <IconTooltip
                    label={
                      thumbCaptured
                        ? t('meshThumbnailUpdated')
                        : t('meshSetThumbnail')
                    }
                  >
                    <button
                      type="button"
                      onClick={onRegenerateThumbnail}
                      disabled={!data}
                      className={styles.iconButton}
                      aria-label={t('meshSetThumbnail')}
                    >
                      {thumbCaptured ? (
                        // Brief confirmation: checkmark
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        // Camera: "capture a thumbnail from the live view"
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                          <circle cx="12" cy="13" r="4" />
                        </svg>
                      )}
                    </button>
                  </IconTooltip>
                )}
                {data?.optimizedSourceUrl ? (
                  <>
                    <button
                      type="button"
                      onClick={onDownloadOriginal}
                      disabled={!data}
                      className={`${styles.iconButton} ${styles.downloadLabelBtn}`}
                    >
                      <DownloadIcon />
                      <span className={styles.downloadBtnLabel}>
                        {t('meshDownloadOriginal')}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={onDownloadOptimized}
                      disabled={!data}
                      className={`${styles.iconButton} ${styles.downloadLabelBtn}`}
                    >
                      <DownloadIcon />
                      <span className={styles.downloadBtnLabel}>
                        {t('meshDownloadOptimized')}
                      </span>
                    </button>
                  </>
                ) : (
                  <IconTooltip label={t('meshDownload')}>
                    <button
                      type="button"
                      onClick={onDownloadOriginal}
                      disabled={!data}
                      className={styles.iconButton}
                      aria-label={t('meshDownload')}
                    >
                      <DownloadIcon />
                    </button>
                  </IconTooltip>
                )}
                {onPlace && (
                  <button
                    type="button"
                    onClick={handlePlace}
                    disabled={!data}
                    className={styles.primaryButton}
                  >
                    {t('meshPlaceInScene')}
                  </button>
                )}
              </div>
            </Tooltip.Provider>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Attribution block — switches between a compact read-only view (composed
// string + clickable source link) and an inline editor for license / author /
// source URL. Title is intentionally absent; the asset doc's `name` field
// (Display name above this block) is the canonical title.
const AttributionBlock = ({
  attribution,
  setAttribution,
  editing,
  onEnterEdit,
  onCancel,
  onFieldKeyDown,
  isOwner,
  disabled
}) => {
  const t = useSharedMessages();
  const composed = composeAttributionString(attribution);
  const hasAnything =
    composed ||
    attribution.source ||
    attribution.sourceName ||
    attribution.generator;

  if (!editing) {
    return (
      <div className={styles.attributionGroup}>
        <div className={styles.attributionHeader}>
          <span>{t('meshAttribution')}</span>
          {isOwner && (
            <button
              type="button"
              className={styles.attributionEditBtn}
              onClick={onEnterEdit}
              disabled={disabled}
            >
              {t('meshEdit')}
            </button>
          )}
        </div>
        {hasAnything ? (
          <AttributionView attribution={attribution} composed={composed} />
        ) : (
          <div className={styles.attributionEmpty}>
            {t('meshNoAttribution')}
            {isOwner && ` ${t('meshNoAttributionHint')}`}
          </div>
        )}
      </div>
    );
  }

  const setField = (key) => (e) =>
    setAttribution((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className={styles.attributionGroup}>
      <div className={styles.attributionHeader}>
        <span>{t('meshAttribution')}</span>
        <button
          type="button"
          className={styles.attributionEditBtn}
          onClick={onCancel}
          disabled={disabled}
        >
          {t('cancel')}
        </button>
      </div>
      <div className={styles.attributionFields}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="meshAttrAuthor">
            {t('meshAuthor')}
          </label>
          <input
            id="meshAttrAuthor"
            type="text"
            className={styles.fieldInput}
            value={attribution.author}
            onChange={setField('author')}
            onKeyDown={onFieldKeyDown}
            disabled={disabled}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="meshAttrLicense">
            {t('meshLicense')}
          </label>
          <input
            id="meshAttrLicense"
            type="text"
            className={styles.fieldInput}
            value={attribution.license}
            onChange={setField('license')}
            onKeyDown={onFieldKeyDown}
            disabled={disabled}
            placeholder={t('meshLicensePlaceholder')}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="meshAttrSource">
            {t('meshSourceUrl')}
          </label>
          <input
            id="meshAttrSource"
            type="url"
            className={styles.fieldInput}
            value={attribution.source}
            onChange={setField('source')}
            onKeyDown={onFieldKeyDown}
            disabled={disabled}
            placeholder="https://…"
          />
        </div>
      </div>
      {/* Read-only context — where the file came from. Surfacing this in
          the editor reminds the user what was auto-detected without making
          it look editable (it's derived from the source URL / generator). */}
      {(attribution.sourceName || attribution.generator) && (
        <div className={styles.attributionContext}>
          {attribution.sourceName && (
            <span>
              <span className={styles.metaLabel}>{t('meshFieldSource')}</span>
              {attribution.sourceName}
            </span>
          )}
          {attribution.generator && (
            <span>
              <span className={styles.metaLabel}>
                {t('meshFieldGenerator')}
              </span>
              {attribution.generator}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

AttributionBlock.propTypes = {
  attribution: PropTypes.object.isRequired,
  setAttribution: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  onEnterEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onFieldKeyDown: PropTypes.func,
  isOwner: PropTypes.bool,
  disabled: PropTypes.bool
};

// Source URLs come from user-editable input and arbitrary GLB metadata, so
// only render http(s) links — block javascript:, data:, and other schemes
// that could execute on click.
const safeHref = (url) => {
  if (typeof url !== 'string') return null;
  try {
    // No base URL: requires an absolute URL. A bare path like "/admin" or
    // "foo/bar" would otherwise resolve against window.location.origin and
    // render as a link back into our own app.
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch {
    // not a parseable absolute URL
  }
  return null;
};

const AttributionView = ({ attribution, composed }) => {
  const t = useSharedMessages();
  const { source, sourceName } = attribution;
  const linkLabel = sourceName
    ? t('meshViewOn', { source: sourceName })
    : t('meshViewSource');
  const href = safeHref(source);
  return (
    <div className={styles.attributionView}>
      {composed && <div className={styles.attributionComposed}>{composed}</div>}
      {href && (
        <a
          className={styles.attributionLink}
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          {linkLabel} →
        </a>
      )}
      {sourceName && !href && (
        <div className={styles.attributionSourceName}>{sourceName}</div>
      )}
    </div>
  );
};

AttributionView.propTypes = {
  attribution: PropTypes.object.isRequired,
  composed: PropTypes.string
};

MeshDetailsModal.propTypes = {
  assetId: PropTypes.string.isRequired,
  ownerUid: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  // Optional: called with the new asset doc after "Copy to my library"
  // succeeds, from the run registry (copyRuns.js) so it fires whether or
  // not the modal is still open. The editor's entity panel uses it to swap
  // the copy into the scene; the gallery leaves it undefined (nothing to
  // swap). A truthy return switches the success copy.
  onCopied: PropTypes.func,
  // Optional: when provided, renders a "Place in scene" CTA. Called with
  // { assetId, ownerUid, storageUrl, name, type } when the user clicks it;
  // the modal closes itself after invoking. Only the gallery card open
  // path passes this — the props-panel "Details" button leaves it
  // undefined (the entity is already in the scene).
  onPlace: PropTypes.func,
  currentIndex: PropTypes.number,
  totalItems: PropTypes.number,
  onNavigate: PropTypes.func
};

export default MeshDetailsModal;
